import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FormikHelpers, useFormik } from 'formik';
import { Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'ramda';

import { Address } from 'src/types/address';
import { User } from 'src/types/resources/User';
import { attributesToSubmit, ContactInfoFormData, initialValues, validationSchema } from 'src/forms/contactInfoForm';
import UsersPresenter from 'src/presenters/UsersPresenter';
import {
  extractResponseErrors,
  isAxiosError,
  isUnprocessedEntityError,
  parseToFormikErrors,
} from 'src/utils/responseErrors';
import { formatPhoneNumberForSubmit } from 'src/utils/phoneNumber';
import useUsers from 'src/hooks/useUsers';
import useModals from 'src/hooks/useModals';
import useEngagements from 'src/hooks/talents/useEngagements';
import { useFeatureFlags } from 'src/hooks';
import Box from 'src/components/Box';
import PhoneNumberField from 'src/components/PhoneNumberField';
import VerificationCodeField from 'src/components/VerificationCodeField';
import AddressAutocomplete from 'src/components/AddressAutocomplete';
import { useSendCodeMutation, useVerifyCodeMutation } from 'src/services/PhoneVerification';
import { ReactComponent as RevuudApprovedIcon } from 'src/assets/images/revuudApprovedIcon.svg';

import styles from './styles';

const isPhoneMaskComplete = (phone: string) => phone && !phone.includes('_');

const SEND_CODE_DEBOUNCE_MS = 800;

type ContactInfoFormProps = {
  autoFocusFieldName?: keyof User;
};

const ContactInfoForm: React.FC<ContactInfoFormProps> = props => {
  const { autoFocusFieldName } = props;

  const { partialUpdateUser, currentUser } = useUsers();
  const { hideModal } = useModals();
  const { loadEngagements, isEngagementsLoadFinished } = useEngagements();
  const { featureEnableTalentPhoneVerification } = useFeatureFlags();
  const [sendCode, { isLoading: phoneStartLoading }] = useSendCodeMutation();
  const [verifyCode, { isLoading: checkCodeLoading }] = useVerifyCodeMutation();

  const hasTalentAccess = UsersPresenter.hasTalentAccess(currentUser);

  const originalPhone = currentUser.phone ? formatPhoneNumberForSubmit(currentUser.phone) : '';

  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeError, setVerificationCodeError] = useState('');
  const [phoneVerificationError, setPhoneVerificationError] = useState('');
  const [showVerificationCodeField, setShowVerificationCodeField] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [lastStartedPhone, setLastStartedPhone] = useState<string | null>(null);
  const [lastVerifiedPhone, setLastVerifiedPhone] = useState<string | null>(null);

  const handleSubmit = async (formData: ContactInfoFormData, { setErrors }: FormikHelpers<ContactInfoFormData>) => {
    const params = attributesToSubmit(formData);
    try {
      await partialUpdateUser(params).unwrap();
      hideModal();
    } catch (error) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        const errors = extractResponseErrors(error);
        setErrors(parseToFormikErrors(errors));
      }
    }
  };

  const {
    values,
    errors,
    submitForm,
    setValues,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
  } = useFormik<ContactInfoFormData>({
    initialValues: initialValues(currentUser),
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const debouncedSendCode = useDebouncedCallback((formattedPhone: string) => {
    setLastStartedPhone(formattedPhone);
    sendCode({ phone: formattedPhone })
      .unwrap()
      .then(() => {
        setShowVerificationCodeField(true);
        setPhoneVerificationError('');
      })
      .catch(error => {
        setShowVerificationCodeField(false);
        setPhoneVerificationError('Verification failed. Please try again.');
        setFieldTouched('phone', true);
        // eslint-disable-next-line no-console
        console.log('error', error);
      });
  }, SEND_CODE_DEBOUNCE_MS);

  const currentFormattedPhone = formatPhoneNumberForSubmit(values.phone);
  const isPhoneChanged = currentFormattedPhone !== originalPhone;
  const needsVerification = featureEnableTalentPhoneVerification && hasTalentAccess && isPhoneChanged;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    if (!featureEnableTalentPhoneVerification || !hasTalentAccess) return;

    const newPhone = e.target.value;
    const formattedPhone = formatPhoneNumberForSubmit(newPhone);
    if (!formattedPhone) return;

    // If phone reverted to original, reset verification state
    if (formattedPhone === originalPhone) {
      setShowVerificationCodeField(false);
      setPhoneVerified(false);
      setVerificationCode('');
      setVerificationCodeError('');
      setPhoneVerificationError('');
      return;
    }

    if (formattedPhone !== lastStartedPhone && formattedPhone !== lastVerifiedPhone) {
      setShowVerificationCodeField(false);
      setPhoneVerified(false);
      setVerificationCode('');
      setVerificationCodeError('');
      setPhoneVerificationError('');
    }

    if (formattedPhone === lastStartedPhone && !phoneVerificationError) {
      setShowVerificationCodeField(true);
    }

    if (formattedPhone === lastVerifiedPhone) {
      setPhoneVerified(true);
    }

    const shouldSendCode =
      isPhoneMaskComplete(newPhone) && formattedPhone !== lastStartedPhone && formattedPhone !== lastVerifiedPhone;
    if (shouldSendCode) {
      debouncedSendCode(formattedPhone);
    }
  };

  useEffect(() => () => debouncedSendCode.cancel(), [debouncedSendCode]);

  const handleVerifyCodeContinue = async () => {
    const formattedPhone = formatPhoneNumberForSubmit(values.phone);
    if (!formattedPhone) return;
    const code = verificationCode.replace(/\D/g, '');
    if (code.length !== 6) return;

    try {
      await verifyCode({ phone: formattedPhone, code }).unwrap();
      setPhoneVerified(true);
      setLastVerifiedPhone(formattedPhone);
      setVerificationCode('');
      setVerificationCodeError('');
    } catch (err) {
      setVerificationCodeError('Verification failed. Please try again.');
      // eslint-disable-next-line no-console
      console.log('error', err);
    }
  };

  let phoneInputEndAdornment: React.ReactNode;
  if (needsVerification && phoneStartLoading) {
    phoneInputEndAdornment = (
      <Box sx={{ display: 'inline-flex' }}>
        <CircularProgress size={20} />
      </Box>
    );
  } else if (needsVerification && phoneVerified) {
    phoneInputEndAdornment = (
      <Box sx={{ color: theme => theme.palette.orange5, display: 'inline-flex' }}>
        <RevuudApprovedIcon />
      </Box>
    );
  }

  const handleAddressChange = (address: Address) => {
    setValues({
      ...values,
      address1: address.streetLine,
      address2: address.secondary,
      city: address.city,
      state: address.state,
      zipCode: address.zipcode,
    });
  };

  useEffect(() => {
    if (hasTalentAccess) {
      loadEngagements()
        .unwrap()
        .then(response => {
          setFieldValue('isTalentEngagementsEmpty', isEmpty(response.results));
        })
        .catch(() => {
          setFieldValue('isTalentEngagementsEmpty', false);
        });
    }
  }, []);

  const isSaveDisabled =
    isSubmitting || (hasTalentAccess && !isEngagementsLoadFinished) || (needsVerification && !phoneVerified);

  return (
    <>
      <Box sx={styles.wrapper}>
        <Typography variant="h2" sx={styles.title}>
          Contact Info
        </Typography>
        <Box sx={styles.row}>
          <TextField
            id="firstName"
            label="First Name"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.firstName}
            error={touched.firstName && Boolean(errors.firstName)}
            helperText={touched.firstName && errors.firstName}
          />
        </Box>
        <Box sx={styles.row}>
          <TextField
            id="lastName"
            label="Last Name"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lastName}
            error={touched.lastName && Boolean(errors.lastName)}
            helperText={touched.lastName && errors.lastName}
          />
        </Box>
        <Box sx={styles.row}>
          <PhoneNumberField
            id="phone"
            label="Mobile Phone"
            value={values.phone}
            onChange={handlePhoneChange}
            onBlur={handleBlur}
            error={touched.phone && Boolean(errors.phone || phoneVerificationError)}
            helperText={touched.phone && (errors.phone || phoneVerificationError)}
            autoFocus={Boolean(autoFocusFieldName === 'phone')}
            isCaptionVisible={!UsersPresenter.hasSuperAdminAccess(currentUser)}
            InputProps={{
              endAdornment: phoneInputEndAdornment,
            }}
          />
        </Box>
        {needsVerification && showVerificationCodeField && !phoneVerified && (
          <Box sx={styles.row}>
            <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <VerificationCodeField
                  value={verificationCode}
                  onChange={newValue => {
                    setVerificationCode(newValue.value);
                  }}
                  error={Boolean(verificationCodeError)}
                  helperText={verificationCodeError}
                  fullWidth
                />
              </Box>
              <Box>
                <LoadingButton
                  type="button"
                  variant="contained"
                  disabled={!verificationCode || verificationCode.replace(/\D/g, '').length !== 6 || checkCodeLoading}
                  onClick={handleVerifyCodeContinue}
                  sx={{
                    ...styles.verificationContinueButton,
                    marginBottom: verificationCodeError ? '19px' : 0,
                  }}
                  loading={checkCodeLoading}
                >
                  Continue
                </LoadingButton>
              </Box>
            </Stack>
          </Box>
        )}
        <Box sx={styles.row}>
          <AddressAutocomplete
            initialValue={values.address1}
            onChangeAddress={handleAddressChange}
            setFieldValue={setFieldValue}
            setFieldError={setFieldError}
            setFieldTouched={setFieldTouched}
            error={touched.address1 && errors.address1}
          />
        </Box>
        <Box sx={styles.row}>
          <TextField
            id="address2"
            label="Address Line 2"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address2}
            error={touched.address2 && Boolean(errors.address2)}
            helperText={touched.address2 && errors.address2}
          />
        </Box>
        <Box sx={styles.row}>
          <TextField
            id="city"
            label="City"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.city}
            error={touched.city && Boolean(errors.city)}
            helperText={touched.city && errors.city}
          />
        </Box>
        <Box sx={styles.row}>
          <TextField
            id="state"
            label="State"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.state}
            error={touched.state && Boolean(errors.state)}
            helperText={touched.state && errors.state}
          />
        </Box>
        <Box sx={styles.row}>
          <TextField
            id="zipCode"
            label="ZIP"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.zipCode}
            error={touched.zipCode && Boolean(errors.zipCode)}
            helperText={touched.zipCode && errors.zipCode}
          />
        </Box>
        <Box sx={styles.buttons}>
          <Button variant="contained" onClick={submitForm} sx={styles.button} disabled={isSaveDisabled}>
            Save
          </Button>
          <Button variant="outlined" onClick={hideModal} sx={styles.button}>
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ContactInfoForm;
